import { encode, type Field, toBase64 } from '../protobuf';
import type { Engine } from '../types';
import { buildUrl } from '../url';

// YouTube's `sp` param is a base64 protobuf. Field 1 is the sort order; field
// 2 is a sub-message of filters. Values follow the filter menu as of the
// January 2026 redesign (durations changed, upload-date sort was removed).
const UPLOAD: Record<string, number> = { today: 2, week: 3, month: 4, year: 5 };
const TYPE: Record<string, number> = {
  video: 1,
  channel: 2,
  playlist: 3,
  movie: 4,
  shorts: 9,
};
const DURATION: Record<string, number> = { short: 4, medium: 5, long: 2 };
const FEATURES: Array<[id: string, field: number]> = [
  ['hd', 4],
  ['subtitles', 5],
  ['creativeCommons', 6],
  ['threeD', 7],
  ['live', 8],
  ['k4', 14],
  ['vr360', 15],
  ['location', 23],
  ['hdr', 25],
  ['vr180', 26],
];

export function youtubeSp(values: Record<string, unknown>): string | undefined {
  const filters: Field[] = [];
  const upload = UPLOAD[String(values.upload)];
  const type = TYPE[String(values.type)];
  const duration = DURATION[String(values.duration)];
  if (upload) filters.push([1, upload]);
  if (type) filters.push([2, type]);
  if (duration) filters.push([3, duration]);
  for (const [id, field] of FEATURES)
    if (values[id] === true) filters.push([field, 1]);

  const message: Field[] = [];
  if (values.sort === 'popularity') message.push([1, 3]);
  if (filters.length) message.push([2, filters]);
  return message.length ? toBase64(encode(message)) : undefined;
}

// Quality and format features describe videos, not channels or playlists.
const videoOnly = {
  when: { param: 'type', in: ['video', 'shorts', 'movie'], orUnset: true },
  whenLabel: 'only applies to videos',
};

export const youtube: Engine = {
  id: 'youtube',
  name: 'YouTube',
  bang: 'yt',
  aliases: ['youtube'],
  homepage: 'https://www.youtube.com',
  category: 'Video',
  defaultOn: true,
  description: 'Videos with length, date, type and quality filters.',
  params: [
    {
      id: 'duration',
      when: { param: 'type', in: ['video', 'movie'], orUnset: true },
      whenLabel: 'only applies to videos and movies',
      type: 'choice',
      label: 'Length',
      group: 'Filters',
      options: [
        { value: 'short', token: 'short', label: 'Under 3 min' },
        { value: 'medium', token: 'med', label: '3–20 min' },
        { value: 'long', token: 'long', label: 'Over 20 min' },
      ],
    },
    {
      id: 'upload',
      when: { param: 'type', in: ['video', 'shorts', 'movie'], orUnset: true },
      whenLabel: 'doesn’t apply to channels or playlists',
      type: 'choice',
      label: 'Uploaded',
      group: 'Filters',
      options: [
        { value: 'today', token: 'd', label: 'Today' },
        { value: 'week', token: 'w', label: 'This week' },
        { value: 'month', token: 'm', label: 'This month' },
        { value: 'year', token: 'y', label: 'This year' },
      ],
    },
    {
      id: 'type',
      type: 'choice',
      label: 'Type',
      group: 'Type',
      options: [
        { value: 'video', token: 'vid', label: 'Videos' },
        { value: 'shorts', token: 'shorts', label: 'Shorts' },
        { value: 'channel', token: 'chan', label: 'Channels' },
        { value: 'playlist', token: 'pl', label: 'Playlists' },
        { value: 'movie', token: 'movie', label: 'Movies' },
      ],
    },
    {
      id: 'sort',
      type: 'choice',
      label: 'Prioritize',
      group: 'Sort',
      help: 'YouTube removed sort-by-date in 2026; popularity is the only alternative to relevance.',
      options: [{ value: 'popularity', token: 'pop', label: 'Popularity' }],
    },
    {
      id: 'k4',
      type: 'flag',
      token: '4k',
      label: '4K',
      group: 'Features',
      ...videoOnly,
    },
    {
      id: 'hd',
      type: 'flag',
      token: 'hd',
      label: 'HD',
      group: 'Features',
      ...videoOnly,
    },
    {
      id: 'hdr',
      type: 'flag',
      token: 'hdr',
      label: 'HDR',
      group: 'Features',
      ...videoOnly,
    },
    {
      id: 'subtitles',
      type: 'flag',
      token: 'cc',
      label: 'Subtitles/CC',
      group: 'Features',
      ...videoOnly,
    },
    {
      id: 'live',
      type: 'flag',
      token: 'live',
      label: 'Live',
      group: 'Features',
      ...videoOnly,
    },
    {
      id: 'creativeCommons',
      type: 'flag',
      token: 'cclic',
      label: 'Creative Commons',
      group: 'Features',
      ...videoOnly,
    },
    {
      id: 'vr360',
      type: 'flag',
      token: '360',
      label: '360°',
      group: 'Features',
      ...videoOnly,
    },
    {
      id: 'vr180',
      type: 'flag',
      token: 'vr180',
      label: 'VR180',
      group: 'Features',
      ...videoOnly,
    },
    {
      id: 'threeD',
      type: 'flag',
      token: '3d',
      label: '3D',
      group: 'Features',
      ...videoOnly,
    },
    {
      id: 'location',
      type: 'flag',
      token: 'loc',
      label: 'Location',
      group: 'Features',
      ...videoOnly,
    },
  ],
  build({ query, values }) {
    return buildUrl('https://www.youtube.com/results', {
      search_query: query,
      sp: youtubeSp(values),
    });
  },
};
